import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router, ActivatedRoute } from "@angular/router";
import { NbAuthService } from "@nebular/auth";
import { AuraOpsService } from "../services/auraOps.service";
import { AURA_OPS } from "../configs/constants.config";

@Component({
  selector: "ngx-sync-ops",
  templateUrl: "./sync-ops.component.html",
  styleUrls: ["./sync-ops.component.scss"],
})
export class SyncOpsComponent implements OnInit {
  public form: FormGroup;
  public loading = false;
  public error: string | null = null;
  public tokenExpired = false;
  public success = false;
  public auraUrl = AURA_OPS.url;
  public isExistingUser = false;
  private passwordPattern =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&.\-_#])[A-Za-z\d@$!%*?&.\-_#]{6,}$/;
  private logo: string;
  private validatedEmail: string | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private authService: NbAuthService,
    private route: ActivatedRoute,
    private auraOpsService: AuraOpsService
  ) {
    this.form = this.formBuilder.group({});
    this.logo = `/assets/images/gds_logo_new.png`;
  }

  ngOnInit() {
    const spinner = document.getElementById("nb-global-spinner");
    if (spinner) {
      spinner.style.display = "none"; // Ocultar el spinner
    }

    this.form = this.formBuilder.group({
      name: [null],
      email: [null],
      password: [null],
      confirmation: [null],
    });

    this.route.queryParams.subscribe((params) => {
      if (params.token) {
        this.error = null;
        this.validateToken(params.token);
      } else {
        this.router.navigate(["/"]);
      }
    });
  }

  validateToken(token: string) {
    this.loading = true;
    this.auraOpsService.resolveInvitationToken({ token }).subscribe({
      next: (response) => {
        if (response.success) {
          this.validatedEmail = response.email.recipient;
          this.isExistingUser = !!response.existingUser;

          if (this.isExistingUser) {
            // For existing users, only email and password are required
            this.form = this.formBuilder.group({
              name: [response.existingUser.fullName],
              email: [
                response.email.recipient,
                [Validators.required, Validators.email],
              ],
              password: [
                null,
                [Validators.required, Validators.pattern(this.passwordPattern)],
              ],
            });
          } else {
            // For new users, all fields are required
            this.form = this.formBuilder.group({
              name: [null, Validators.required],
              email: [
                response.email.recipient,
                [Validators.required, Validators.email],
              ],
              password: [
                null,
                [Validators.required, Validators.pattern(this.passwordPattern)],
              ],
              confirmation: [
                null,
                [Validators.required, Validators.pattern(this.passwordPattern)],
              ],
            });
          }

          this.form.updateValueAndValidity();

          this.loading = false;
          this.error = null;
          this.tokenExpired = false;
          return;
        }

        const statusCode = (response as any).statusCode || 500;

        if (statusCode === 410 || statusCode === 404) {
          this.tokenExpired = true;
          this.error = null;
          this.loading = false;
        } else if (statusCode === 400) {
          this.error = (response as any).message;
          this.tokenExpired = false;
          this.loading = false;
        } else {
          this.error = "Hubo un error al validar el token.";
          this.tokenExpired = false;
          this.loading = false;
        }
      },
      error: (err) => {
        const statusCode = err.status || 500;

        if (statusCode === 410 || statusCode === 404) {
          this.tokenExpired = true;
          this.error = null;
        } else {
          this.error = err.error
            ? err.error.message
            : "Hubo un error al validar el token.";
          this.tokenExpired = false;
        }
        this.loading = false;
      },
    });
  }

  onSubmit(form: FormGroup) {
    if (form.invalid) return;
    const input = form.value;

    if (!this.isExistingUser && input.password !== input.confirmation) {
      this.error = "Las contraseñas no coinciden.";
      return;
    }

    if (input.email !== this.validatedEmail) {
      this.error = "El correo electrónico no es válido.";
      return;
    }

    this.loading = true;
    this.error = null;
    this.success = false;

    const payload = {
      fullName: input.name,
      email: input.email,
      password: input.password,
      token: this.route.snapshot.queryParams.token,
    };

    this.auraOpsService.syncPolicy(payload).subscribe({
      next: (response) => {
        if (response.success) {
          this.authService
            .authenticate("email", {
              email: input.email,
              password: input.password,
            })
            .subscribe({
              next: (authResult) => {
                if (authResult.isSuccess()) {
                  this.router.navigate(["/user/dashboard"]);
                } else {
                  this.error =
                    "Hubo un error en la autenticación después del registro.";
                }
                this.loading = false;
              },
              error: () => {
                this.error =
                  "Hubo un error en la autenticación después del registro.";
                this.loading = false;
              },
            });

          return;
        }

        const statusCode = response.statusCode || 500;

        if (statusCode === 401) {
          this.error = "Credenciales inválidas.";
          this.loading = false;
        } else if ([400, 404].includes(statusCode)) {
          this.error = response.message;
          this.loading = false;
        } else {
          this.error = "Hubo un error en el proceso de registro.";
          this.loading = false;
        }
      },
      error: (err) => {
        this.error = err.error
          ? err.error.message
          : "Hubo un error en el proceso de registro.";
        this.loading = false;
      },
    });
  }
}
